import {
  createContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
  useContext,
} from "react";
import type { Brand } from "../shared/types";
import { useAuth } from "./AuthContext";
import { getBrandById } from "../shared/services/apis";

// Context type
type BrandContextType = {
  loading: boolean;
  brand: Brand | null;
  refreshBrand: () => Promise<void>;
  updateBrand: (updatedBrand: Brand) => void;
};

// Create context
const BrandContext = createContext<BrandContextType | undefined>(undefined);

// Provider component
export const BrandProvider = ({ children }: { children: ReactNode }) => {
  const auth = useAuth();
  const [brand, setBrand] = useState<Brand | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const fetchBrandById = useCallback(async () => {
    if (!auth.user?.brandId) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const response = await getBrandById(auth.user.brandId,
          true // Include relations if needed 
      );
      setBrand(response);
    } catch (error) {
      console.error("Failed to fetch brand:", error);
    } finally {
      setLoading(false);
    }
  }, [auth.user?.brandId]);

  const refreshBrand = useCallback(async () => {
    await fetchBrandById();
  }, [fetchBrandById]);

  const updateBrand = useCallback((updatedBrand: Brand) => {
    setBrand(updatedBrand);
  }, []);

  useEffect(() => {
    fetchBrandById();
  }, [fetchBrandById]);

  const contextValue = useMemo(() => ({
    brand,
    loading,
    refreshBrand,
    updateBrand,
  }), [brand, loading, refreshBrand, updateBrand]);

  return (
    <BrandContext.Provider value={contextValue}>
      {children}
    </BrandContext.Provider>
  );
};

// Custom hook to use the brand context
export const useBrand = () => {
  const context = useContext(BrandContext);
  if (context === undefined) {
    throw new Error("useBrand must be used within a BrandProvider");
  }
  return context;
};
